import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  doc,
  setDoc,
  onSnapshot,
  Timestamp,
  updateDoc
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { useOutlet } from '../../context/OutletContext';

const SettingsContext = createContext();

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within SettingsProvider');
  }
  return context;
};

const defaultSettings = {
  restaurantName: 'Odisha Zaika',
  phone: '7873611363',
  email: 'contact@odishazaika.com',
  address: 'Bhubaneswar, Odisha, India',
  shopOnline: true, // Shop status: true = online, false = offline
  deliveryCharge: 50,
  minimumOrder: 200,
  freeDeliveryAbove: 500,
  gstPercent: 5,
  packagingCharge: 10,
  deliveryRadius: 10,
  estimatedDeliveryTime: '30-45 mins',
  operatingHours: {
    monday: { open: '09:00', close: '22:00', closed: false },
    tuesday: { open: '09:00', close: '22:00', closed: false },
    wednesday: { open: '09:00', close: '22:00', closed: false },
    thursday: { open: '09:00', close: '22:00', closed: false },
    friday: { open: '09:00', close: '22:00', closed: false },
    saturday: { open: '09:00', close: '22:00', closed: false },
    sunday: { open: '09:00', close: '22:00', closed: false }
  },
  paymentMethods: {
    cod: true,
    online: false,
    card: false
  },
  notifications: {
    emailOnNewOrder: true,
    soundAlert: true,
    whatsappNotifications: false,
    smsNotifications: false
  }
};

export const SettingsProvider = ({ children }) => {
  const { selectedOutlet } = useOutlet(); // Get selected outlet
  const [settings, setSettings] = useState(defaultSettings);
  const [loading, setLoading] = useState(true);

  // Load settings from selected outlet in Firebase
  useEffect(() => {
    // Wait for outlet to be selected
    if (!selectedOutlet) {
      setSettings(defaultSettings);
      setLoading(false);
      return;
    }

    console.log(`⚙️ Loading settings for outlet: ${selectedOutlet.name}`);

    const outletRef = doc(db, 'outlets', selectedOutlet.id);

    // Set up real-time listener
    const unsubscribe = onSnapshot(
      outletRef,
      (snapshot) => {
        try {
          if (snapshot.exists()) {
            const outletData = snapshot.data();
            // Merge outlet settings with default settings
            const outletSettings = {
              ...defaultSettings,
              ...outletData.settings,
              restaurantName: outletData.name,
              phone: outletData.phone || defaultSettings.phone,
              email: outletData.email || defaultSettings.email,
              address: outletData.address || defaultSettings.address
            };
            console.log(`✅ Settings loaded for ${outletData.name}`);
            setSettings(outletSettings);
          } else {
            console.warn(`⚠️ Outlet ${selectedOutlet.id} not found in database`);
            setSettings(defaultSettings);
          }
          setLoading(false);
        } catch (error) {
          console.error('Error loading settings:', error);
          setSettings(defaultSettings);
          setLoading(false);
        }
      },
      (error) => {
        console.error('Error in settings listener:', error);
        setSettings(defaultSettings);
        setLoading(false);
      }
    );

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [selectedOutlet]);

  // Save settings to Firebase (outlet-specific)
  const saveSettings = async (newSettings) => {
    try {
      if (!selectedOutlet) {
        return { success: false, error: 'No outlet selected' };
      }

      const outletRef = doc(db, 'outlets', selectedOutlet.id);

      // Update outlet's settings field
      await updateDoc(outletRef, {
        'settings': newSettings,
        'updatedAt': Timestamp.now()
      });

      console.log(`✅ Settings saved for ${selectedOutlet.name}`);
      setSettings(newSettings);
      return { success: true };
    } catch (error) {
      console.error('Error saving settings:', error);
      return { success: false, error };
    }
  };

  // Update specific setting
  const updateSetting = async (key, value) => {
    try {
      const newSettings = { ...settings, [key]: value };
      return await saveSettings(newSettings);
    } catch (error) {
      console.error('Error updating setting:', error);
      return { success: false, error };
    }
  };

  const value = {
    settings,
    loading,
    saveSettings,
    updateSetting
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};
